import clsx from 'clsx';
import { ImageIcon } from 'lucide-react';
import { AppCard } from '@/shared/components/ui/AppCard/AppCard';
import { useCampaignWizardStore } from '../../../stores/campaign-wizard-store';
import { resolveSlotImageUrls } from '../../../logic/creative-media';
import { CTA_OPTIONS } from '../../../types/campaigns.types';
import { CreativeType } from '@/shared/types/enums';
import styles from './AdPreviewPanel.module.scss';

/**
 * Ad mock pinned to the right of the campaign wizard. Conditionally mounted
 * starting at the creative step. Reads straight from the wizard store so it
 * stays live across the steps where it is visible — the Creative step syncs
 * edits into the store as the user types. Renders a pending/empty state with
 * placeholders until the organizer fills things in.
 */
export function AdPreviewPanel() {
  const formData = useCampaignWizardStore((s) => s.formData);
  const creative = formData.creatives?.[0];

  const brand = formData.eventName || formData.name || 'Your Event';
  const headline = creative?.headlines?.[0] ?? '';
  const description = creative?.descriptions?.[0] ?? '';
  const ctaLabel =
    CTA_OPTIONS.find((c) => c.value === creative?.callToAction)?.label ?? 'Learn More';
  const isVideo = creative?.type === CreativeType.VIDEO;
  const videoUrl = creative?.videoPreview ?? null;
  const carouselFirstImage =
    creative?.type === CreativeType.CAROUSEL
      ? (creative?.carouselCards?.find((card) => card.imageUrl)?.imageUrl ?? null)
      : null;
  const mediaUrl =
    carouselFirstImage ??
    resolveSlotImageUrls(creative?.generatedImages, creative?.mediaPreviews)[0] ??
    null;

  return (
    <aside className={styles.panel} aria-label="Ad preview">
      <span className={styles.panelLabel}>Preview</span>
      <AppCard className={styles.adPreview}>
        <div className={styles.previewHeader}>
          <div className={styles.previewAvatar} />
          <div className={styles.previewMeta}>
            <span className={styles.previewBrand}>{brand}</span>
            <span className={styles.previewSponsored}>Sponsored</span>
          </div>
        </div>
        <div className={styles.previewMedia}>
          {isVideo && videoUrl ? (
            <video src={videoUrl} className={styles.previewImg} controls preload="metadata" />
          ) : mediaUrl ? (
            <img src={mediaUrl} alt="Ad preview" className={styles.previewImg} />
          ) : (
            <div className={styles.previewMediaEmpty}>
              <ImageIcon size={28} />
              <span>Your {isVideo ? 'video' : 'image'} appears here</span>
            </div>
          )}
        </div>
        <div className={styles.previewBody}>
          <h3 className={clsx(styles.previewHeadline, !headline && styles.placeholder)}>
            {headline || 'Your headline here'}
          </h3>
          <p className={clsx(styles.previewDescription, !description && styles.placeholder)}>
            {description || 'Your ad description will appear as you type'}
          </p>
        </div>
        <div className={styles.previewCta}>{ctaLabel}</div>
      </AppCard>
    </aside>
  );
}
